import React, { useEffect, useMemo, useState } from "react";
import { Button, Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions, Field, Input, Select, Switch } from "@fluentui/react-components";
import {
  Add20Regular, ArrowSync20Regular, Camera20Regular, Copy20Regular, Dismiss20Regular, FullScreenMaximize20Regular, Grid20Regular,
  ArrowUp20Regular, ArrowDown20Regular, ArrowLeft20Regular, ArrowRight20Regular, ZoomIn20Regular, ZoomOut20Regular,
  PlugConnected20Regular, Person20Regular, DoorArrowRight20Regular, Alert20Regular, Search20Regular, VideoOff20Regular, Play20Filled, Record20Filled,
} from "@fluentui/react-icons";
import styles from "./Hyperview.module.css";

/* ------------------------------------------------------------------ *
 * Hyper View: live CCTV wall for hotel owners.
 *
 * API wiring points (search for "API:"):
 *   1. CAMERAS      - GET  /hospitality/cameras            (list of cameras linked to the property)
 *   2. getStreamUrl - POST /hospitality/cameras/:id/stream (returns a short-lived signed WebRTC/HLS URL)
 *   3. addCamera    - POST /hospitality/cameras            (registers a camera with the property gateway)
 *   4. EVENTS       - GET  /hospitality/cameras/events     (motion / person / door events)
 * Until those exist the wall plays a simulated feed so the UI can be reviewed.
 * ------------------------------------------------------------------ */

type Status = "online" | "offline";
type Kind = "motion" | "person" | "door";
interface Cam { id: string; name: string; zone: string; status: Status; ptz: boolean; res: string; kbps: number; latency: number; hue: number; src?: string; motion?: string }
interface Ev { id: string; cam: string; kind: Kind; at: number; text: string } // at = minutes since midnight

const CAMERAS: Cam[] = [ // API: GET /hospitality/cameras
  { id: "c1", name: "Main entrance", zone: "Lobby", status: "online", ptz: false, res: "1080p", kbps: 1450, latency: 420, hue: 210 },
  { id: "c2", name: "Reception desk", zone: "Lobby", status: "online", ptz: false, res: "1080p", kbps: 1320, latency: 380, hue: 200 },
  { id: "c3", name: "Car park A", zone: "Outdoors", status: "online", ptz: true, res: "4MP", kbps: 2100, latency: 510, hue: 160, motion: "Motion 2 min ago" },
  { id: "c4", name: "Car park B", zone: "Outdoors", status: "offline", ptz: true, res: "4MP", kbps: 0, latency: 0, hue: 150 },
  { id: "c5", name: "Pool deck", zone: "Outdoors", status: "online", ptz: true, res: "1080p", kbps: 1750, latency: 460, hue: 190 },
  { id: "c6", name: "Restaurant", zone: "Food & drink", status: "online", ptz: false, res: "1080p", kbps: 1280, latency: 400, hue: 30 },
  { id: "c7", name: "Bar", zone: "Food & drink", status: "online", ptz: false, res: "720p", kbps: 860, latency: 430, hue: 20 },
  { id: "c8", name: "Corridor 2nd floor", zone: "Corridors", status: "online", ptz: false, res: "1080p", kbps: 1180, latency: 390, hue: 250, motion: "Motion 11 min ago" },
  { id: "c9", name: "Corridor 3rd floor", zone: "Corridors", status: "online", ptz: false, res: "1080p", kbps: 1210, latency: 395, hue: 260 },
  { id: "c10", name: "Back gate", zone: "Back of house", status: "online", ptz: false, res: "1080p", kbps: 1500, latency: 450, hue: 120, motion: "Gate opened 24 min ago" },
];
const EVENTS: Ev[] = [ // API: GET /hospitality/cameras/events
  { id: "e1", cam: "c1", kind: "person", at: 6 * 60 + 12, text: "Person at main entrance" },
  { id: "e2", cam: "c10", kind: "door", at: 7 * 60 + 40, text: "Back gate opened" },
  { id: "e3", cam: "c3", kind: "motion", at: 9 * 60 + 5, text: "Motion in car park A" },
  { id: "e4", cam: "c6", kind: "person", at: 12 * 60 + 30, text: "Restaurant busy" },
  { id: "e5", cam: "c8", kind: "motion", at: 14 * 60 + 18, text: "Motion in corridor" },
  { id: "e6", cam: "c10", kind: "door", at: 18 * 60 + 52, text: "Back gate opened" },
  { id: "e7", cam: "c1", kind: "person", at: 21 * 60 + 3, text: "Person at main entrance" },
];
// API: replace with a signed WebRTC (WHEP) or LL-HLS URL from your gateway; an empty result keeps the simulated feed.
const getStreamUrl = async (_cam: Cam): Promise<string | undefined> => undefined;

const pad = (n: number) => String(n).padStart(2, "0");
const hhmm = (m: number) => `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;
const LAYOUTS = [{ k: 1, label: "1" }, { k: 2, label: "2×2" }, { k: 3, label: "3×3" }];

type SceneKind = "lobby" | "car" | "pool" | "dining" | "corridor" | "gate";
const kindOf = (c: Cam): SceneKind => (c.zone === "Lobby" ? "lobby" : c.zone === "Corridors" ? "corridor" : c.zone === "Food & drink" ? "dining" : c.zone === "Back of house" ? "gate" : /pool/i.test(c.name) ? "pool" : "car");

/* Illustrated stand-in for the live picture (viewBox 160x90). Replace by the <video> once a stream URL exists. */
const Person: React.FC<{ x: number; y: number; s?: number; fill?: string; cls?: string }> = ({ x, y, s = 1, fill = "#1b2230", cls }) => (
  <g className={cls} style={{ transformBox: "fill-box" }}>
    <g transform={`translate(${x} ${y}) scale(${s})`}><circle cx="0" cy="-15" r="3.2" fill={fill} /><rect x="-3.6" y="-11.5" width="7.2" height="12" rx="3" fill={fill} /><rect x="-3" y="0" width="2.6" height="8" rx="1.2" fill={fill} /><rect x="0.4" y="0" width="2.6" height="8" rx="1.2" fill={fill} /></g>
  </g>
);
const Scene: React.FC<{ kind: SceneKind; hue: number }> = ({ kind, hue }) => {
  const h = hue;
  return (
    <svg className={styles.svg} viewBox="0 0 160 90" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      {kind === "lobby" && (<>
        <rect width="160" height="90" fill={`hsl(${h} 30% 78%)`} /><rect y="58" width="160" height="32" fill={`hsl(${h} 18% 62%)`} />
        <rect x="0" y="0" width="160" height="10" fill={`hsl(${h} 25% 70%)`} />
        {[18, 52, 108, 142].map((x) => <rect key={x} x={x} y="14" width="10" height="44" fill={`hsl(${h} 35% 90%)`} opacity=".75" />)}
        <rect x="52" y="52" width="56" height="12" rx="2" fill={`hsl(${h} 25% 34%)`} /><rect x="52" y="49" width="56" height="4" rx="1" fill={`hsl(${h} 30% 48%)`} />
        <rect x="76" y="40" width="16" height="9" rx="1" fill="#222b3a" /><rect x="78" y="41.5" width="12" height="6" fill="#7db3e8" opacity=".8" />
        <ellipse cx="22" cy="66" rx="6" ry="2" fill="#0003" /><rect x="19" y="52" width="6" height="14" rx="2" fill="#8a5a3a" /><circle cx="22" cy="49" r="8" fill="#2f7a4a" />
        <Person x={30} y={74} s={1.15} cls={styles.walkA} /><Person x={128} y={72} s={1.05} fill="#2a3a55" cls={styles.walkB} />
      </>)}
      {kind === "car" && (<>
        <rect width="160" height="90" fill={`hsl(${h + 40} 35% 20%)`} /><rect y="32" width="160" height="58" fill="#2b3036" />
        <rect y="30" width="160" height="4" fill="#3a424b" />
        {[16, 56, 96, 136].map((x) => <rect key={x} x={x} y="50" width="2" height="40" fill="#e9edf2" opacity=".55" transform={`skewX(${x < 80 ? 14 : -14})`} />)}
        <g><rect x="22" y="48" width="30" height="13" rx="4" fill="#c7ccd4" /><rect x="28" y="43" width="18" height="8" rx="3" fill="#8fa0b4" /><circle cx="29" cy="62" r="3" fill="#111" /><circle cx="46" cy="62" r="3" fill="#111" /></g>
        <g><rect x="96" y="52" width="32" height="14" rx="4" fill="#a33c3c" /><rect x="102" y="46" width="19" height="9" rx="3" fill="#7d8da1" /><circle cx="104" cy="67" r="3" fill="#111" /><circle cx="121" cy="67" r="3" fill="#111" /></g>
        <g className={styles.drive}><rect x="0" y="70" width="26" height="11" rx="4" fill="#2f5d9e" /><rect x="5" y="65" width="15" height="7" rx="3" fill="#6f8fb8" /><circle cx="6" cy="82" r="2.6" fill="#111" /><circle cx="19" cy="82" r="2.6" fill="#111" /><rect x="24" y="73" width="6" height="3" fill="#ffe9a8" /></g>
        <rect x="83" y="8" width="2" height="26" fill="#47505c" /><circle cx="84" cy="8" r="4" fill="#ffe9a8" opacity=".9" />
      </>)}
      {kind === "pool" && (<>
        <rect width="160" height="90" fill={`hsl(${h} 45% 82%)`} /><rect y="0" width="160" height="26" fill={`hsl(${h + 20} 30% 70%)`} />
        <rect y="26" width="160" height="64" fill="#d9d2c4" /><rect x="14" y="36" width="132" height="44" rx="6" fill="#4fb5d9" /><rect x="14" y="36" width="132" height="44" rx="6" fill="none" stroke="#f4f1ea" strokeWidth="2.5" />
        {[44, 54, 64, 72].map((y, i) => <rect key={y} className={styles.ripple} style={{ animationDelay: `${i * -1.1}s` }} x="22" y={y} width="116" height="1.6" rx="1" fill="#fff" opacity=".45" />)}
        {[24, 44, 64].map((x) => <g key={x}><rect x={x} y="82" width="12" height="4" rx="1.5" fill="#fff" /><rect x={x} y="80" width="5" height="3" rx="1" fill="#e9eef5" /></g>)}
        <rect x="118" y="14" width="3" height="22" fill="#8a5a3a" /><ellipse cx="119.5" cy="12" rx="12" ry="5" fill="#2f7a4a" />
        <Person x={92} y={62} s={0.9} fill="#2a3a55" cls={styles.swim} />
      </>)}
      {kind === "dining" && (<>
        <rect width="160" height="90" fill={`hsl(${h} 45% 30%)`} /><rect y="60" width="160" height="30" fill={`hsl(${h} 30% 20%)`} />
        {[62, 96, 124].map((x) => <g key={x}><line x1={x} y1="0" x2={x} y2="14" stroke="#e7c27a" strokeWidth=".6" /><circle cx={x} cy="16" r="4" fill="#ffd98a" /><circle cx={x} cy="16" r="10" fill="#ffd98a" opacity=".18" /></g>)}
        {[[24, 64], [66, 70], [106, 64], [140, 72]].map(([x, y]) => <g key={`${x}-${y}`}><ellipse cx={x} cy={y} rx="12" ry="4" fill="#f4e9d6" /><rect x={x - 1} y={y} width="2" height="10" fill="#6b4a2f" /><circle cx={x - 5} cy={y - 2} r="1.6" fill="#fff" opacity=".8" /></g>)}
        <rect x="0" y="34" width="160" height="2" fill="#0003" />
        <Person x={50} y={58} s={1} fill="#1b1410" cls={styles.walkA} /><Person x={118} y={56} s={.95} fill="#3a2a22" cls={styles.walkB} />
      </>)}
      {kind === "corridor" && (<>
        <rect width="160" height="90" fill={`hsl(${h} 18% 24%)`} />
        <polygon points="0,0 160,0 108,30 52,30" fill={`hsl(${h} 15% 34%)`} /><polygon points="0,90 160,90 108,62 52,62" fill={`hsl(${h} 12% 16%)`} />
        <polygon points="0,0 52,30 52,62 0,90" fill={`hsl(${h} 20% 28%)`} /><polygon points="160,0 108,30 108,62 160,90" fill={`hsl(${h} 20% 22%)`} />
        <rect x="52" y="30" width="56" height="32" fill={`hsl(${h} 25% 40%)`} />
        {[12, 24].map((x, i) => <polygon key={x} points={`${x},${14 + i * 4} ${x + 10},${22 + i * 2} ${x + 10},${64 - i * 2} ${x},${74 - i * 4}`} fill="#101520" opacity=".7" />)}
        {[150, 138].map((x, i) => <polygon key={x} points={`${x},${14 + i * 4} ${x - 10},${22 + i * 2} ${x - 10},${64 - i * 2} ${x},${74 - i * 4}`} fill="#101520" opacity=".7" />)}
        <polygon points="68,3 92,3 86,12 74,12" fill="#fff6d6" opacity=".9" /><polygon points="64,56 96,56 108,90 52,90" fill="#fff6d6" opacity=".06" />
        <Person x={80} y={52} s={.75} fill="#0d1018" cls={styles.approach} />
      </>)}
      {kind === "gate" && (<>
        <rect width="160" height="90" fill={`hsl(${h + 60} 28% 18%)`} /><rect y="56" width="160" height="34" fill="#2a2f2b" />
        {Array.from({ length: 17 }, (_, i) => <rect key={i} x={i * 9.6 + 2} y="24" width="2" height="34" fill="#9aa5b1" opacity=".75" />)}
        <rect y="24" width="160" height="2.4" fill="#9aa5b1" /><rect y="54" width="160" height="2.4" fill="#9aa5b1" />
        <g className={styles.gateSwing}><rect x="64" y="22" width="32" height="38" fill="none" stroke="#c6ced8" strokeWidth="2" /><rect x="70" y="26" width="2" height="30" fill="#c6ced8" /><rect x="80" y="26" width="2" height="30" fill="#c6ced8" /><rect x="90" y="26" width="2" height="30" fill="#c6ced8" /></g>
        <rect x="12" y="6" width="2" height="22" fill="#47505c" /><polygon points="13,10 -6,56 32,56" fill="#ffe9a8" opacity=".12" />
      </>)}
    </svg>
  );
};

const Feed: React.FC<{ cam: Cam; live: boolean; zoom?: number; pan?: [number, number]; thumb?: boolean }> = ({ cam, live, zoom = 1, pan = [0, 0], thumb }) => {
  const [url, setUrl] = useState<string | undefined>(cam.src);
  useEffect(() => { let on = true; if (cam.status === "online" && !thumb) getStreamUrl(cam).then((u) => on && u && setUrl(u)); return () => { on = false; }; }, [cam, thumb]);
  if (cam.status === "offline") return <div className={styles.off} />;
  return (
    <div className={`${styles.scene} ${thumb ? styles.thumb : ""} ${live ? "" : styles.recorded}`}>
      {url ? <video className={styles.vid} src={url} autoPlay muted playsInline /> : (
        <div className={styles.sim} style={{ transform: `translate(${pan[0] * 9}%, ${pan[1] * 9}%) scale(${zoom})` }}><Scene kind={kindOf(cam)} hue={cam.hue} /></div>
      )}
      {!thumb && <><div className={styles.vignette} /><div className={styles.scan} /></>}
    </div>
  );
};

type StatusFilter = "all" | "online" | "offline" | "activity";
const GAPS: [number, number][] = [[190, 215]]; // API: minutes with no recording (camera or gateway was offline)

export const HyperView: React.FC<{ propertyName?: string }> = ({ propertyName = "Cilpron Grand Hotel" }) => {
  const [cams, setCams] = useState<Cam[]>(CAMERAS);
  const [layout, setLayout] = useState(3);
  const [zone, setZone] = useState("All");
  const [state, setState] = useState<StatusFilter>("all");
  const [q, setQ] = useState("");
  const [focus, setFocus] = useState<string | null>(null);
  const [live, setLive] = useState(true);
  const [clock, setClock] = useState(new Date());
  const nowMin = clock.getHours() * 60 + clock.getMinutes();
  const [at, setAt] = useState(nowMin);
  const [ptz, setPtz] = useState<{ z: number; p: [number, number] }>({ z: 1, p: [0, 0] });
  const [adding, setAdding] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  useEffect(() => { const t = setInterval(() => setClock(new Date()), 1000); return () => clearInterval(t); }, []);
  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(null), 3000); return () => clearTimeout(t); }, [toast]);
  useEffect(() => { setPtz({ z: 1, p: [0, 0] }); }, [focus]);

  const zones = useMemo(() => ["All", ...Array.from(new Set(cams.map((c) => c.zone)))], [cams]);
  const inZone = cams.filter((c) => (zone === "All" || c.zone === zone) && c.name.toLowerCase().includes(q.toLowerCase()));
  const counts = { all: inZone.length, online: inZone.filter((c) => c.status === "online").length, offline: inZone.filter((c) => c.status === "offline").length, activity: inZone.filter((c) => c.motion).length };
  const shown = inZone.filter((c) => state === "all" || (state === "online" && c.status === "online") || (state === "offline" && c.status === "offline") || (state === "activity" && c.motion));
  const online = cams.filter((c) => c.status === "online").length;
  const fc = cams.find((c) => c.id === focus) ?? null;
  const per = layout * layout;
  const page = fc ? [] : shown.slice(0, per);
  const t0 = live ? nowMin : at;
  const stamp = live ? `${clock.toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}  ${clock.toLocaleTimeString("en-GB")}` : `${clock.toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}  ${hhmm(at)}:00`;
  const snap = (c: Cam) => setToast(`Snapshot of ${c.name} saved`);
  const reconnect = (id: string) => { setCams((l) => l.map((c) => (c.id === id ? { ...c, status: "online", kbps: 1800, latency: 480 } : c))); setToast("Camera reconnected"); };
  const seek = (m: number) => { setAt(Math.min(m, nowMin)); setLive(false); }; // nothing is recorded in the future
  const goLive = () => { setLive(true); setAt(nowMin); };
  const evs = EVENTS.filter((e) => !fc || e.cam === fc.id);
  const icon = (k: Kind) => (k === "person" ? <Person20Regular /> : k === "door" ? <DoorArrowRight20Regular /> : <Alert20Regular />);
  const pct = (m: number) => `${(m / 1439) * 100}%`;
  const filters: [StatusFilter, string][] = [["all", "All"], ["online", "Online"], ["offline", "Offline"], ["activity", "Activity"]];

  const tile = (c: Cam, big = false) => (
    <div key={c.id} className={`${styles.tile} ${big ? styles.tileBig : ""} ${c.status === "offline" ? styles.tileOff : ""}`}>
      <div role={big ? undefined : "button"} tabIndex={big ? -1 : 0} aria-label={big ? undefined : `Open ${c.name}`} className={styles.tileMain} onClick={() => !big && c.status === "online" && setFocus(c.id)} onKeyDown={(e) => { if (!big && c.status === "online" && e.key === "Enter") setFocus(c.id); }}>
        <Feed cam={c} live={live} zoom={big ? ptz.z : 1} pan={big ? ptz.p : [0, 0]} />
        <div className={styles.top}>
          <div className={styles.who}><span className={styles.name}>{c.name}</span><span className={styles.zone}>{c.zone}</span></div>
          {c.status === "online" && (live ? <span className={styles.live}><i />Live</span> : <span className={styles.rec}><Play20Filled />Recorded</span>)}
        </div>
        {c.status === "offline" && (
          <div className={styles.offMsg}><VideoOff20Regular /><strong>No signal</strong><span>The gateway can't reach this camera.</span><Button size="small" appearance="primary" icon={<ArrowSync20Regular />} onClick={() => reconnect(c.id)}>Reconnect</Button></div>
        )}
        {c.status === "online" && (
          <div className={styles.foot}>
            <span className={styles.stamp}>{stamp}</span>
            {c.motion && <span className={styles.motion}><Alert20Regular />{c.motion}</span>}
            <span className={styles.acts}>
              <button type="button" aria-label="Snapshot" onClick={(e) => { e.stopPropagation(); snap(c); }}><Camera20Regular /></button>
              {!big && <button type="button" aria-label="Open" onClick={(e) => { e.stopPropagation(); setFocus(c.id); }}><FullScreenMaximize20Regular /></button>}
            </span>
          </div>
        )}
      </div>
    </div>
  );

  const padBtn = (cls: string, label: string, ic: React.ReactNode, fn: () => void) => (<button type="button" className={`${styles.pb} ${cls}`} aria-label={label} onClick={fn}>{ic}</button>);

  return (
       <div className={styles.gU1Tf}>
        <div className={styles.jUsfu}>
          <div className={styles.QIl4y}>
    <div className={styles.page}>
      <div className={styles.head}>
        <div className={styles.title}>
          <h2>Hyper View</h2>
          <p>{propertyName}. {online} of {cams.length} cameras are streaming{live ? " live" : ""}.</p>
        </div>
        <div className={styles.tools}>
          <div className={styles.seg} role="group" aria-label="Mode">
            <button className={live ? styles.segOn : ""} onClick={goLive}>Live</button>
            <button className={!live ? styles.segOn : ""} onClick={() => setLive(false)}>Playback</button>
          </div>
          {!fc && (
            <div className={styles.seg} role="group" aria-label="Layout">
              {LAYOUTS.map((l) => (<button key={l.k} className={layout === l.k ? styles.segOn : ""} onClick={() => setLayout(l.k)}>{l.label}</button>))}
            </div>
          )}
          <Button size="small" appearance="primary" icon={<Add20Regular />} onClick={() => setAdding(true)}>Add camera</Button>
        </div>
      </div>

      <div className={styles.body}>
        <aside className={styles.side}>
          <div className={styles.search}><Search20Regular /><input placeholder="Search cameras" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search cameras" /></div>
          <div className={styles.zones}>{zones.map((z) => (<button key={z} className={zone === z ? styles.zoneOn : ""} onClick={() => { setZone(z); setFocus(null); }}>{z}</button>))}</div>
          <div className={styles.list}>
            {inZone.map((c) => (
              <button key={c.id} className={`${styles.row} ${focus === c.id ? styles.rowOn : ""}`} onClick={() => (c.status === "online" ? setFocus(c.id) : setFocus(null))}>
                <span className={styles.th}><Feed cam={c} live thumb /></span>
                <span className={styles.rn}><b>{c.name}</b><small>{c.status === "online" ? c.zone : "Offline"}</small></span>
                <i className={c.status === "online" ? styles.dotOn : styles.dotOff} />
              </button>
            ))}
            {inZone.length === 0 && <p className={styles.empty}>No cameras match. Clear the search or pick another zone.</p>}
          </div>
          <div className={styles.gw}>
            <PlugConnected20Regular /><div><strong>Hotel gateway</strong><small>Connected. Cameras stay on your hotel network.</small></div><i className={styles.dotOn} />
          </div>
        </aside>

        <section className={styles.main}>
          {!fc && (
            <div className={styles.filters} role="tablist" aria-label="Camera status">
              {filters.map(([k, l]) => (<button key={k} role="tab" aria-selected={state === k} className={state === k ? styles.fOn : ""} onClick={() => setState(k)}>{l}<span>{counts[k]}</span></button>))}
            </div>
          )}
          {!fc && <div className={`${styles.grid} ${styles["g" + layout]}`}>{page.map((c) => tile(c))}</div>}
          {!fc && shown.length === 0 && <p className={styles.empty}>Nothing to show for this filter.</p>}
          {!fc && shown.length > per && <p className={styles.more}>Showing {per} of {shown.length}. Pick a zone or a bigger layout to see the rest.</p>}
          {fc && (
            <div className={styles.focus}>
              <div className={styles.focusTop}><Button size="small" icon={<Dismiss20Regular />} onClick={() => setFocus(null)}>Back to wall</Button><span>{fc.name} · {fc.zone}</span></div>
              <div className={styles.focusGrid}>
                {tile(fc, true)}
                <div className={styles.ctrl}>
                  {fc.ptz && (
                    <div className={styles.blk}>
                      <h4>Move camera</h4>
                      <div className={styles.joy}>
                        {padBtn(styles.pu, "Tilt up", <ArrowUp20Regular />, () => setPtz((s) => ({ ...s, p: [s.p[0], Math.min(s.p[1] + 1, 2)] })))}
                        {padBtn(styles.pl, "Pan left", <ArrowLeft20Regular />, () => setPtz((s) => ({ ...s, p: [Math.min(s.p[0] + 1, 2), s.p[1]] })))}
                        <button type="button" className={styles.ph} onClick={() => setPtz({ z: 1, p: [0, 0] })}>Home</button>
                        {padBtn(styles.pr, "Pan right", <ArrowRight20Regular />, () => setPtz((s) => ({ ...s, p: [Math.max(s.p[0] - 1, -2), s.p[1]] })))}
                        {padBtn(styles.pd, "Tilt down", <ArrowDown20Regular />, () => setPtz((s) => ({ ...s, p: [s.p[0], Math.max(s.p[1] - 1, -2)] })))}
                      </div>
                      <div className={styles.zoom}>
                        {padBtn("", "Zoom out", <ZoomOut20Regular />, () => setPtz((s) => ({ ...s, z: Math.max(+(s.z - 0.25).toFixed(2), 1) })))}
                        <div className={styles.zbar}><i style={{ width: `${((ptz.z - 1) / 2) * 100}%` }} /></div>
                        {padBtn("", "Zoom in", <ZoomIn20Regular />, () => setPtz((s) => ({ ...s, z: Math.min(+(s.z + 0.25).toFixed(2), 3) })))}
                      </div>
                    </div>
                  )}
                  <div className={styles.blk}>
                    <h4>Stream health</h4>
                    <dl className={styles.dl}><dt>Resolution</dt><dd>{fc.res}</dd><dt>Bitrate</dt><dd>{(fc.kbps / 1000).toFixed(1)} Mbps</dd><dt>Delay</dt><dd>{fc.latency} ms</dd></dl>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className={styles.tl}>
            <div className={styles.tlHead}>
              <strong>{fc ? `${fc.name} timeline` : "Today across all cameras"}</strong>
              <span>{live ? "Watching live" : `Viewing ${hhmm(at)}`}</span>
              {!live && <Button size="small" appearance="primary" icon={<Record20Filled />} onClick={goLive}>Go live</Button>}
            </div>
            <div className={styles.track}>
              <div className={styles.rail}>
                <i className={styles.cover} style={{ width: pct(nowMin) }} />
                {GAPS.map(([s, e]) => <i key={s} className={styles.gap} style={{ left: pct(s), width: pct(e - s) }} title="No recording" />)}
              </div>
              {evs.filter((e) => e.at <= nowMin + 1440).map((e) => (
                <button key={e.id} type="button" className={styles.pin} style={{ left: pct(e.at) }} aria-label={`${hhmm(e.at)} ${e.text}`} title={`${hhmm(e.at)} ${e.text}`} onClick={() => { if (!fc) setFocus(e.cam); seek(e.at); }}>{icon(e.kind)}</button>
              ))}
              <i className={styles.head2} style={{ left: pct(t0) }}><b>{hhmm(t0)}</b></i>
              <input type="range" min={0} max={1439} value={Math.min(t0, nowMin)} aria-label="Playback time" onChange={(e) => seek(Number(e.target.value))} />
            </div>
            <div className={styles.ticks}>{["00:00", "06:00", "12:00", "18:00", "24:00"].map((x) => <span key={x}>{x}</span>)}</div>
            <div className={styles.events}>
              {evs.map((e) => (
                <button key={e.id} onClick={() => { if (!fc) setFocus(e.cam); seek(e.at); }}>
                  <span className={styles.evIc}>{icon(e.kind)}</span><span>{e.text}</span><small>{hhmm(e.at)}</small>
                </button>
              ))}
              {evs.length === 0 && <p className={styles.empty}>No events for this camera today.</p>}
            </div>
          </div>
        </section>
      </div>

      <AddCamera open={adding} onClose={() => setAdding(false)} zones={zones.filter((z) => z !== "All")} onAdd={(c) => { setCams((l) => [...l, c]); setAdding(false); setToast(`${c.name} added`); }} />
      {toast && <div className={styles.toast} role="status" aria-live="polite">{toast}</div>}
    </div>
    </div>
    </div>
   </div>
  );
};

const AddCamera: React.FC<{ open: boolean; onClose: () => void; zones: string[]; onAdd: (c: Cam) => void }> = ({ open, onClose, zones, onAdd }) => {
  const [name, setName] = useState("");
  const [zone, setZone] = useState(zones[0] ?? "Lobby");
  const [type, setType] = useState("rtsp");
  const [addr, setAddr] = useState("");
  const [user, setUser] = useState("");
  const [pass, setPass] = useState("");
  const [ptz, setPtz] = useState(false);
  const [test, setTest] = useState<"idle" | "busy" | "ok" | "bad">("idle");
  useEffect(() => { if (open) { setName(""); setAddr(""); setUser(""); setPass(""); setPtz(false); setTest("idle"); } }, [open]);
  const hint = type === "rtsp" ? "rtsp://192.168.1.20:554/stream1" : type === "onvif" ? "192.168.1.20" : type === "nvr" ? "192.168.1.5 (channel 3)" : "Device ID or serial number";
  const runTest = () => { setTest("busy"); setTimeout(() => setTest(addr.trim().length > 5 ? "ok" : "bad"), 900); }; // API: POST /hospitality/cameras/test
  const ready = name.trim() && addr.trim() && test === "ok";
  return (
    <Dialog open={open} onOpenChange={(_, d) => { if (!d.open) onClose(); }}>
      <DialogSurface className={styles.dlg}>
        <DialogBody>
          <DialogTitle action={<Button appearance="subtle" size="small" icon={<Dismiss20Regular />} aria-label="Close" onClick={onClose} />}>Add camera</DialogTitle>
          <DialogContent>
            <p className={styles.dlgHint}>The camera connects through your hotel gateway. Nothing on your network is opened to the internet.</p>
            <div className={styles.form}>
              <Field label="Camera name" required><Input value={name} onChange={(_, d) => setName(d.value)} placeholder="e.g. Pool deck" /></Field>
              <Field label="Zone"><Select value={zone} onChange={(_, d) => setZone(d.value)}>{zones.map((z) => <option key={z}>{z}</option>)}<option>New zone</option></Select></Field>
              <Field label="Connection type"><Select value={type} onChange={(_, d) => { setType(d.value); setTest("idle"); }}><option value="rtsp">RTSP stream URL</option><option value="onvif">ONVIF camera (auto-detect)</option><option value="nvr">NVR / DVR channel</option><option value="cloud">Cloud camera (Hikvision, Dahua, EZVIZ)</option></Select></Field>
              <Field label={type === "rtsp" ? "Stream URL" : "Address"} required><Input value={addr} onChange={(_, d) => { setAddr(d.value); setTest("idle"); }} placeholder={hint} /></Field>
              <div className={styles.two}>
                <Field label="Username"><Input value={user} onChange={(_, d) => setUser(d.value)} autoComplete="off" /></Field>
                <Field label="Password"><Input type="password" value={pass} onChange={(_, d) => setPass(d.value)} autoComplete="new-password" /></Field>
              </div>
              <Switch checked={ptz} onChange={(_, d) => setPtz(d.checked)} label="Camera can pan, tilt and zoom" />
              <div className={styles.testRow}>
                <Button size="small" onClick={runTest} disabled={!addr.trim() || test === "busy"}>{test === "busy" ? "Testing…" : "Test connection"}</Button>
                {test === "ok" && <span className={styles.ok}>Connected · 1080p · 1.4 Mbps</span>}
                {test === "bad" && <span className={styles.warn}>Could not reach the camera. Check the address and login.</span>}
              </div>
              <div className={styles.pair}>
                <div><strong>Pair a new gateway</strong><small>Install the Cilpron gateway on a PC or mini-PC at the hotel, then enter this code.</small></div>
                <code>CPN-4F7K-92QD</code>
                <Button size="small" icon={<Copy20Regular />} aria-label="Copy pairing code" onClick={() => navigator.clipboard?.writeText("CPN-4F7K-92QD")} />
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <Button size="small" onClick={onClose}>Cancel</Button>
            <Button size="small" appearance="primary" disabled={!ready} onClick={() => onAdd({ id: `c${Date.now()}`, name: name.trim(), zone, status: "online", ptz, res: "1080p", kbps: 1400, latency: 450, hue: 200 })}>Add camera</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default HyperView;